import { useEffect, useState } from 'react';
import { ChatOverlay, ChatPanel } from '../components/Chat.tsx';
import { api } from '../lib/api.ts';
import { useStreamStatus } from '../lib/useStreamStatus.ts';

/** Transparent page background, for an OBS Browser Source. */
function useTransparentPage() {
  useEffect(() => {
    document.documentElement.style.background = 'transparent';
    document.body.style.background = 'transparent';
  }, []);
}

/**
 * /s/<id>/chat: the chat on its own, for a second screen while streaming from OBS. Opened from the
 * OBS page in the creator's browser, so host tools work there.
 */
export function ChatPage({ id }: { id: string }) {
  const { stream, error } = useStreamStatus(id);
  useEffect(() => {
    document.title = 'Live chat · ZIP2LIVE';
  }, []);
  return (
    <main className="h-[100dvh] bg-navy-900 p-2 text-white sm:p-3">
      {stream && !stream.removed ? (
        <ChatPanel stream={stream} className="h-full rounded-2xl" />
      ) : (
        <p className="p-8 text-center font-semibold text-white/70">{stream?.removed ? 'This broadcast was removed.' : error ? 'Stream not found.' : 'Loading chat…'}</p>
      )}
    </main>
  );
}

/**
 * /obs/chat/<channel id>: chat messages on a transparent page for an OBS Browser Source. It follows
 * the channel's current broadcast, so it is added to OBS once. ?fade=30 hides messages after 30 s,
 * ?max=8 shows at most 8.
 */
export function ObsChatOverlay({ channelId }: { channelId: string }) {
  useTransparentPage();
  const params = new URLSearchParams(location.search);
  const [streamId, setStreamId] = useState<string | null>(null);
  useEffect(() => {
    const find = () => api.channelOverlay(channelId).then((r) => setStreamId((cur) => (r.stream ? r.stream.id : cur)), () => undefined);
    void find();
    const t = setInterval(() => void find(), 15_000);
    return () => clearInterval(t);
  }, [channelId]);
  const { stream } = useStreamStatus(streamId);
  if (!stream || stream.removed) return null;
  return <ChatOverlay stream={stream} big max={Number(params.get('max')) || 10} fadeAfter={Number(params.get('fade')) || undefined} className="fixed inset-x-0 bottom-0 p-6" />;
}
